import React, { useState, useEffect, useContext } from 'react';
import { Table, Input, Pagination, PaginationItem, PaginationLink, Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInbox } from '@fortawesome/free-solid-svg-icons';
import { PostData } from '../../../services/FetchData';
import { toast } from 'react-toastify';
import DetailSkeleton from '../../../components/Skeleton/DetailSkeleton';
import { ProductContext } from '../../../store';
import '../AIAssistant/AIAssistantProducts.scss';

const AIAssistantCollectionPages = () => {
    const { currentWebsiteId } = useContext(ProductContext);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [pagesData, setPagesData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalPages, setTotalPages] = useState(0);
    const [seeding, setSeeding] = useState(false);

    useEffect(() => {
        if (currentWebsiteId) {
            fetchCollectionPages(true);
        }
    }, [currentWebsiteId, appliedSearchTerm, currentPage, perPage]);

    const fetchCollectionPages = async (showLoading = true) => {
        const websiteId = currentWebsiteId;
        if (!websiteId) {
            toast.error('No website ID found');
            if (showLoading) setLoading(false);
            return;
        }
        if (showLoading) setLoading(true);
        try {
            const response = await PostData('ms1', 'api/v1/aiassistant/collectionPages', {
                page: currentPage,
                websiteId,
                limit: perPage,
                searchTerm: (appliedSearchTerm || '').trim(),
            });
            if (response !== 'Invalid' && response?.status === 'success') {
                const list = response.data?.pages || [];
                setPagesData(list);
                setTotalPages(Math.ceil((response.data?.total || 0) / perPage) || 0);
            } else {
                toast.error(response?.message || 'Failed to load collection pages');
            }
        } catch (e) {
            toast.error('Failed to load collection pages');
        } finally {
            if (showLoading) setLoading(false);
        }
    };

    const handleSeedCatalog = async () => {
        if (!currentWebsiteId) return;
        setSeeding(true);
        try {
            const response = await PostData('ms1', 'api/v1/aiassistant/seedCollectionPages', {
                websiteId: currentWebsiteId,
            });
            if (response?.status === 'success') {
                toast.success('Collection page catalog seeded');
                await fetchCollectionPages(false);
            } else {
                toast.error(response?.message || 'Seed failed');
            }
        } catch (e) {
            toast.error('Seed failed');
        } finally {
            setSeeding(false);
        }
    };

    const handleToggle = async (pagePath, currentValue) => {
        const websiteId = currentWebsiteId;
        if (!websiteId || !pagePath) return;
        const newState = !currentValue;
        const original = [...pagesData];
        setPagesData((rows) =>
            rows.map((row) =>
                row.pagePath === pagePath ? { ...row, widgetEnable: newState } : row,
            ),
        );
        try {
            const response = await PostData('ms1', 'api/v1/aiassistant/updateCollectionWidgetEnable', {
                websiteId,
                pagePath,
                widgetEnable: newState,
            });
            if (response?.status !== 'success') {
                setPagesData(original);
                toast.error(response?.message || 'Update failed');
            } else {
                toast.success(`Assistant ${newState ? 'enabled' : 'disabled'} for ${pagePath}`);
            }
        } catch (e) {
            setPagesData(original);
            toast.error('Update failed');
        }
    };

    const totalPageCount = totalPages || 1;

    return (
        <div className="ai-assistant-products-page">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <h2 className="mb-0">Collection pages</h2>
                <Button color="secondary" outline disabled={seeding} onClick={handleSeedCatalog}>
                    {seeding ? 'Seeding…' : 'Seed BFRI catalog'}
                </Button>
            </div>
            <p className="text-muted">
                Enable or disable the inline collection assistant per URL (same control model as Products).
            </p>
            <div className="mb-3 d-flex gap-2">
                <Input
                    type="search"
                    placeholder="Search by path or title"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            setAppliedSearchTerm(searchTerm.trim());
                            setCurrentPage(1);
                        }
                    }}
                />
                <Button
                    color="primary"
                    onClick={() => {
                        setAppliedSearchTerm(searchTerm.trim());
                        setCurrentPage(1);
                    }}
                >
                    Search
                </Button>
            </div>
            {loading ? (
                <DetailSkeleton />
            ) : pagesData.length === 0 ? (
                <div className="text-center py-5 text-muted">
                    <FontAwesomeIcon icon={faInbox} size="2x" className="mb-2" />
                    <p>No collection pages indexed. Use &quot;Seed BFRI catalog&quot; for Big Fundraising Ideas URLs.</p>
                </div>
            ) : (
                <>
                    <Table responsive hover className="align-middle">
                        <thead>
                            <tr>
                                <th>Path</th>
                                <th>Title</th>
                                <th>Enable assistant</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pagesData.map((row) => (
                                <tr key={row.pagePath}>
                                    <td><code>{row.pagePath}</code></td>
                                    <td>{row.title}</td>
                                    <td>
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={!!row.widgetEnable}
                                                onChange={() => handleToggle(row.pagePath, row.widgetEnable)}
                                                aria-label={`Toggle assistant for ${row.pagePath}`}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    {totalPageCount > 1 && (
                        <Pagination className="justify-content-center">
                            <PaginationItem disabled={currentPage <= 1}>
                                <PaginationLink previous onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} />
                            </PaginationItem>
                            <PaginationItem active>
                                <PaginationLink>{currentPage}</PaginationLink>
                            </PaginationItem>
                            <PaginationItem disabled={currentPage >= totalPageCount}>
                                <PaginationLink next onClick={() => setCurrentPage((p) => p + 1)} />
                            </PaginationItem>
                        </Pagination>
                    )}
                </>
            )}
        </div>
    );
};

export default AIAssistantCollectionPages;
